import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSesion } from '../estado/SesionContext';
import { Confeti } from '../components/Confeti';
import type { RespuestaElegida, ResultadoIntento, RevisionRespuesta } from '../data/AcademiaRepo';
import type { Curso } from '../domain/types';

export function Quiz() {
  const { cursoId } = useParams<{ cursoId: string }>();
  const { repo, perfil, refrescarPuntos } = useSesion();
  const navegar = useNavigate();

  const [curso, setCurso] = useState<Curso | null>(null);
  const [indice, setIndice] = useState(0);
  const [elegidas, setElegidas] = useState<RespuestaElegida[]>([]);
  const [marcada, setMarcada] = useState<string | null>(null);
  /**
   * Quien decide el acierto es el SERVIDOR, no la pantalla.
   *
   * Antes se pintaba con `opcion.esCorrecta`, y esa propiedad no viaja al navegador con el
   * backend vivo: llegaba vacia siempre, asi que toda respuesta salia en rojo y el cartel
   * decia «La respuesta era otra» incluso al acertar. La nota, que la calcula el servidor,
   * si estaba bien: lo que mentia era la explicacion, que es donde la persona aprende.
   */
  const [revision, setRevision] = useState<RevisionRespuesta | null>(null);
  const [calificando, setCalificando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoIntento | null>(null);

  useEffect(() => {
    if (!repo || !cursoId) return;
    let vivo = true;
    void repo.obtenerCurso(cursoId).then((c) => {
      if (vivo) setCurso(c);
    });
    return () => {
      vivo = false;
    };
  }, [repo, cursoId]);

  if (!curso) return <p className="py-10 text-center text-gris">Cargando…</p>;

  const pregunta = curso.preguntas[indice];
  const esUltima = indice === curso.preguntas.length - 1;

  async function responder(opcionId: string) {
    // Una vez marcada, la respuesta no se cambia. Es la regla de la demo y es la correcta:
    // si se pudiera cambiar tras ver la explicación, la nota no mediría nada.
    if (marcada || !repo) return;
    setMarcada(opcionId);
    setElegidas((prev) => [...prev, { preguntaId: pregunta.id, opcionId }]);
    try {
      setRevision(await repo.revisarRespuesta(opcionId));
    } catch {
      // Si la revisión no llega, no se inventa un veredicto: se muestra la explicación sin
      // decir «correcto» ni «era otra». La nota final la calcula el servidor igual.
      setRevision(null);
    }
  }

  async function avanzar() {
    if (!esUltima) {
      setIndice((i) => i + 1);
      setMarcada(null);
      setRevision(null);
      window.scrollTo(0, 0);
      return;
    }

    // Doble toque en "Terminar" no debe calificar dos veces.
    if (calificando || !repo || !curso) return;
    setCalificando(true);
    try {
      const r = await repo.calificarIntento(curso.id, elegidas);
      setResultado(r);
      await refrescarPuntos();
      window.scrollTo(0, 0);
    } finally {
      setCalificando(false);
    }
  }

  function reintentar() {
    setIndice(0);
    setElegidas([]);
    setMarcada(null);
    setRevision(null);
    setResultado(null);
  }

  if (resultado) {
    return (
      <Resultado
        curso={curso}
        resultado={resultado}
        nombre={perfil?.nombres.trim().split(' ')[0] ?? ''}
        onReintentar={reintentar}
        onRepasar={() => navegar(`/curso/${curso.id}`)}
      />
    );
  }

  return (
    <article>
      <Link to={`/curso/${curso.id}`} className="text-sm font-bold text-azul">
        ← Volver al curso
      </Link>

      <header className="mt-3 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-cielo">
          <div
            className="h-full rounded-full bg-rojo transition-all"
            style={{ width: `${(100 * (indice + 1)) / curso.preguntas.length}%` }}
          />
        </div>
        <span className="shrink-0 text-xs font-extrabold text-gris">
          Pregunta {indice + 1} de {curso.preguntas.length}
        </span>
      </header>

      <section className="mt-5 rounded-2xl border border-borde bg-white p-5 sm:p-7">
        <h2 className="font-titulos text-xl font-extrabold leading-snug text-tinta">
          {pregunta.enunciado}
        </h2>

        <ul className="mt-5 space-y-3">
          {pregunta.opciones.map((opcion) => {
            const estaMarcada = marcada === opcion.id;
            // Del servidor. Mientras la revisión no llegue, no se pinta ningún veredicto.
            const esLaCorrecta = revision?.opcionCorrectaId === opcion.id;
            const revelar = marcada !== null && revision !== null;

            let estilo = 'border-borde bg-white hover:border-azul';
            if (revelar && esLaCorrecta) estilo = 'border-verde bg-verde/10 text-verde';
            else if (revelar && estaMarcada) estilo = 'border-rojo bg-rojo/10 text-rojo';
            else if (revelar) estilo = 'border-borde bg-white opacity-60';

            return (
              <li key={opcion.id}>
                <button
                  onClick={() => responder(opcion.id)}
                  disabled={revelar}
                  // min-h-[44px]: por debajo de eso el dedo no acierta en un celular.
                  className={`flex min-h-[44px] w-full items-center gap-3 rounded-xl border-[1.5px] px-4 py-3 text-left text-sm font-bold transition ${estilo}`}
                >
                  <span className="flex-1">{opcion.texto}</span>
                  {revelar && esLaCorrecta && <span aria-hidden>✓</span>}
                  {revelar && estaMarcada && !esLaCorrecta && <span aria-hidden>✕</span>}
                </button>
              </li>
            );
          })}
        </ul>

        {marcada && (
          <div className="mt-5 rounded-xl bg-cielo px-4 py-3 text-sm leading-relaxed">
            <p className="mb-1 font-extrabold text-azul">
              {revision === null
                ? 'Tu respuesta quedó registrada'
                : revision.correcta
                  ? '¡Correcto! 🎉'
                  : 'La respuesta era otra'}
            </p>
            <p>{pregunta.explicacion}</p>
          </div>
        )}

        {marcada && (
          <button
            onClick={avanzar}
            disabled={calificando}
            className="mt-5 w-full rounded-full bg-rojo py-3.5 text-sm font-extrabold text-white transition hover:bg-rojo-osc disabled:opacity-60"
          >
            {esUltima ? (calificando ? 'Calificando…' : 'Terminar quiz') : 'Siguiente pregunta →'}
          </button>
        )}
      </section>
    </article>
  );
}

function Resultado({
  curso,
  resultado,
  nombre,
  onReintentar,
  onRepasar,
}: {
  curso: Curso;
  resultado: ResultadoIntento;
  nombre: string;
  onReintentar: () => void;
  onRepasar: () => void;
}) {
  const { aprobado, nota, correctas, total, puntosGanados, yaAprobadoAntes } = resultado;

  if (!aprobado) {
    return (
      <section className="rounded-2xl border border-borde bg-white p-8 text-center">
        <p className="text-5xl">💪</p>
        <h1 className="mt-3 font-titulos text-2xl font-extrabold">Nota {nota} — ¡casi!</h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-gris">
          Acertaste {correctas} de {total} en <b>{curso.titulo}</b>. Necesitas{' '}
          {curso.notaMinima} para aprobar. Tu intento quedó registrado: repasa las lecciones
          y vuelve a intentar, no pierdes nada.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={onRepasar}
            className="rounded-full bg-azul px-6 py-3 text-sm font-extrabold text-white"
          >
            Repasar el curso
          </button>
          <button
            onClick={onReintentar}
            className="rounded-full border-[1.5px] border-borde px-6 py-3 text-sm font-extrabold text-gris"
          >
            Reintentar quiz
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-borde bg-white p-8 text-center">
      <Confeti />
      <p className="text-6xl">{curso.insigniaIco}</p>
      <h1 className="mt-3 font-titulos text-2xl font-extrabold">
        ¡Felicitaciones{nombre ? `, ${nombre}` : ''}!
      </h1>
      <p className="mt-2 text-sm text-gris">
        Aprobaste <b>{curso.titulo}</b> con nota <b>{nota}</b> ({correctas}/{total} correctas)
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Chip
          titulo={`${curso.insigniaIco} ${curso.insigniaNombre}`}
          pie={yaAprobadoAntes ? 'INSIGNIA YA GANADA' : 'NUEVA INSIGNIA'}
        />
        <Chip
          titulo={`⭐ +${puntosGanados} puntos`}
          pie={
            puntosGanados === 0
              ? 'YA GANADOS ANTES'
              : nota === 100
                ? 'INCLUYE BONUS POR NOTA PERFECTA'
                : 'SUMADOS A TU SALDO'
          }
        />
      </div>

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Link
          to="/recompensas"
          className="rounded-full bg-rojo px-6 py-3 text-sm font-extrabold text-white"
        >
          Ver mis recompensas 🎁
        </Link>
        <Link
          to="/"
          className="rounded-full border-[1.5px] border-borde px-6 py-3 text-sm font-extrabold text-gris"
        >
          Ir al inicio
        </Link>
      </div>
    </section>
  );
}

function Chip({ titulo, pie }: { titulo: string; pie: string }) {
  return (
    <div className="rounded-2xl border border-borde bg-cielo px-5 py-3">
      <p className="text-sm font-extrabold text-azul">{titulo}</p>
      <p className="mt-0.5 text-[0.6rem] font-extrabold tracking-wide text-gris">{pie}</p>
    </div>
  );
}
